/** @jsx jsx */
import React, { useRef } from 'react';
import { styled, jsx, css } from '@compiled/react';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import InfoIcon from '@atlaskit/icon/core/status-information';
import { Inline, xcss } from '@atlaskit/primitives';
import { components, CheckboxOption, type OptionProps } from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { JiraPopper as Popper } from '@atlassian/jira-popper/src/ui/jira-popper.tsx';
import type { SelectOption } from '../../../../common/types.tsx';
import { CustomOptionStyles } from '../../../../common/ui/picker/styled.tsx';
import { type OptionState, getOptionState } from './utils.tsx';

export type OptionTypeSingleAndMultiSelect = OptionProps<SelectOption, true> & {
	isMulti?: boolean;
};

const tooltipStyles = css({
	boxSizing: 'border-box',
	maxWidth: '240px',
	backgroundColor: token('color.background.neutral.bold'),
	borderRadius: token('radius.small'),
	color: token('color.text.inverse'),
	font: token('font.body.UNSAFE_small'),
	insetBlockStart: token('space.0'),
	insetInlineStart: token('space.0'),
	overflowWrap: 'break-word',
	paddingBlockEnd: token('space.025'),
	paddingBlockStart: token('space.025'),
	paddingInlineEnd: token('space.075'),
	paddingInlineStart: token('space.075'),
	wordWrap: 'break-word',
});

const ConditionalTooltip = ({
	condition,
	content,
	children,
}: {
	condition: boolean;
	content: React.ReactNode;
	children: React.ReactNode;
}) => (condition ? <Tooltip content={content}>{children}</Tooltip> : children);

export const Option = ({
	isMulti = true,
	...props
}: OptionTypeSingleAndMultiSelect): JSX.Element => {
	const iconRef = useRef(null);
	const { isOnlyUnselectable, disabledMessage, invalid } =
		props.data?.optionType === 'option' ||
		props.data?.optionType === 'avatar' ||
		props.data?.optionType === 'lozenge' ||
		props.data?.optionType === 'field'
			? props.data
			: {
					isOnlyUnselectable: undefined,
					disabledMessage: undefined,
					invalid: undefined,
				};

	// Only disable checkbox for an only unselectable option when it is not a selected option
	const optionState = getOptionState(
		props.isDisabled,
		Boolean(isOnlyUnselectable),
		props.isSelected,
		Boolean(invalid),
	);

	const hasIconAfter = () => {
		switch (optionState) {
			case 'only-unselectable-unselected':
			case 'only-unselectable-selected':
			case 'disabled':
				return Boolean(disabledMessage);
			case 'invalid':
				return true;
			default:
				return undefined;
		}
	};

	const OptionInner = (
		<Inline
			spread="space-between"
			alignBlock="center"
			xcss={optionStateAttributes[optionState].xcss}
		>
			<CustomOptionStyles
				hasIconAfter={hasIconAfter()}
				isFocused={props.isFocused}
				isDisabled={props.isDisabled}
				isSingleSelect
			>
				{isMulti ? (
					/* @ts-expect-error - This component has stricter OptionType args which isn't compatible with ErrorOption in SelectOption. */
					<CheckboxOption {...props} />
				) : (
					<components.Option {...props} isMulti={false} />
				)}
			</CustomOptionStyles>
			<TooltipContainer ref={iconRef}>
				{optionState !== 'invalid' && Boolean(disabledMessage) && (
					// Show a tooltip for mouse users, since option.isFocused is not triggered for disabled options on mouse over
					<ConditionalTooltip content={disabledMessage} condition={props.isDisabled}>
						<InfoIcon
							testId="jql-builder-basic-picker.ui.async-select.select.option.info-icon"
							label=""
							spacing="spacious"
							color={token('color.icon')}
						/>
					</ConditionalTooltip>
				)}
				{optionState === 'invalid' && (
					<StatusErrorIcon
						testId="jql-builder-basic-picker.ui.async-select.select.option.invalid-icon"
						label=""
						spacing="spacious"
						color={token('color.icon.danger')}
					/>
				)}
			</TooltipContainer>
		</Inline>
	);

	return (
		<>
			<div data-testid={optionStateAttributes[optionState].testid}>
				{optionState === 'only-unselectable-unselected' ||
				optionState === 'disabled' ||
				optionState === 'invalid' ? (
					<UnsafeStyledCheckboxOption>{OptionInner}</UnsafeStyledCheckboxOption>
				) : (
					OptionInner
				)}
			</div>
			{/* show tooltips via popper because react-select options does not trigger tooltip on keydownEvent https://github.com/JedWatson/react-select/discussions/4915 */}
			{(disabledMessage || (props.selectProps?.invalidLabel && optionState === 'invalid')) &&
				props.isFocused &&
				iconRef.current && (
					<>
						<Popper
							referenceElement={iconRef.current}
							placement="bottom"
							messageType="transactional"
							messageId="jql-builder-basic-picker.ui.async-select.select.option.popper"
						>
							{({ ref, style }) => (
								// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, jira/react/no-style-attribute -- Ignored via go/DSP-18766
								<div ref={ref} style={style} css={tooltipStyles}>
									{optionState === 'invalid' ? props.selectProps?.invalidLabel : disabledMessage}
								</div>
							)}
						</Popper>
					</>
				)}
		</>
	);
};

export default Option;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UnsafeStyledCheckboxOption = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& > div > div > div': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		backgroundColor: 'inherit !important',
	},
});

// These styles allow for the tooltip to sit over the option, so that the
// option width for the hover state is consistent with and without a tooltip
// This is a work-around that will need to have a more robust long-term solution
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const TooltipContainer = styled.div({
	display: 'flex',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginLeft: '-100px',
	paddingRight: token('space.150'),
	height: '24px',
});

const DisabledCheckboxOptionStyles = xcss({
	color: 'color.text.disabled',
});

const InvalidCheckboxOptionStyles = xcss({
	backgroundColor: 'color.background.danger',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const optionStateAttributes: Record<
	OptionState,
	{
		testid: string;
		xcss: ReturnType<typeof xcss>[];
	}
> = {
	disabled: {
		testid: 'jql-builder-basic.ui.jql-editor.picker.async-select.select.option.disabled-option',
		xcss: [DisabledCheckboxOptionStyles],
	},
	invalid: {
		testid: 'jql-builder-basic.ui.jql-editor.picker.async-select.select.option.invalid-option',
		xcss: [InvalidCheckboxOptionStyles],
	},
	selected: {
		testid: 'jql-builder-basic.ui.jql-editor.picker.async-select.select.selected-option',
		xcss: [],
	},
	unselected: {
		testid: 'jql-builder-basic.ui.jql-editor.picker.async-select.select.option',
		xcss: [],
	},
	'only-unselectable-unselected': {
		testid: 'jql-builder-basic.ui.jql-editor.picker.async-select.select.option.only-unselectable',
		xcss: [DisabledCheckboxOptionStyles],
	},
	'only-unselectable-selected': {
		testid:
			'jql-builder-basic.ui.jql-editor.picker.async-select.select.selected-option.only-unselectable',
		xcss: [],
	},
};
